<template>
  <panel ref="panel" :title="`${$tc('tournament.registered_coaches')} (${coaches.length})`" collapsable>
    <template v-slot:actions>
      <el-button
        v-if="canEdit"
        type="primary"
        icon="el-icon-plus"
        size="medium"
        round
        @click="$refs.panel.expanded = true; openManageCoach = true">
        {{ $tc('tournament.register_coach') }}
      </el-button>

      <el-tooltip v-if="coaches.length" :content="$tc('general.print')" placement="top">
        <el-button
          icon="el-icon-printer"
          size="medium"
          circle
          @click="openPrint($tc('club.coach', 2), $refs.printTable ? $refs.printTable.$el : null)" />
      </el-tooltip>
    </template>

    <coaches-table
      :data="coaches"
      :can-edit="canEdit"
      :show-club-name="showClubName"
      custom-logic
      hide-edit
      @edit="openEdit($event)"
      @custom-delete="onRemove($event)" />

    <print-coaches-table ref="printTable" v-show="false" :data="coaches" :show-club-name="showClubName" :tournament="tournament" />

    <main-drawer v-if="openManageCoach" :title="$tc('tournament.registered_coaches')" @close="onClose()">
      <main-select v-model="form.coachId" :label="$tc('club.coach')" :options="coachOptions" :validator="$v.form.coachId" />

      <template v-slot:footer>
        <div class="flex-buttons">
          <el-button @click="onClose()">{{ $tc('general.cancel') }}</el-button>
          <el-button type="primary" @click="addCoach()">{{ $tc('tournament.register_coach') }}</el-button>
        </div>
      </template>
    </main-drawer>
  </panel>
</template>

<script>
import openPrint from '@/common/print'
import { required } from 'vuelidate/lib/validators'
import errorHandler from '@/common/errorHandler.js'

import CoachesTable from './CoachesTable'
import PrintCoachesTable from './PrintCoachesTable'

export default {
  components: {
    CoachesTable,
    PrintCoachesTable
  },

  props: {
    club: {
      type: Object,
      default: null
    },

    tournament: {
      type: Object,
      required: true
    },

    showClubName: {
      type: Boolean,
      default: false
    },

    canEdit: {
      type: Boolean,
      default: false
    },

    coaches: {
      type: Array,
      default: null
    }
  },

  data () {
    return {
      clubId: null,
      form: {
        coachId: null
      },
      openPrint,
      openManageCoach: false
    }
  },

  computed: {
    user () {
      return this.$store.state.auth.user
    },

    coachOptions () {
      const coaches = this.club?.coaches || this.user.club?.coaches || []
      const filteredCoaches = coaches.filter(coach => coach.isActive)

      return filteredCoaches.map(coach => ({
        text: coach.firstName + ' ' + coach.lastName,
        id: coach.id,
        disabled: this.coaches.some(c => c.id === coach.id)
      }))
    }
  },

  methods: {
    onClose () {
      this.form.coachId = null
      this.$v.$reset()
      this.openManageCoach = false
    },

    async addCoach () {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) return
      this.loading = true

      try {
        const tournament = await this.$store.dispatch('tournaments/registerCoach', {
          id: this.tournament?.id,
          coachId: this.form.coachId
        })
        this.updateActiveTournament(tournament)
        this.$message.success(this.$tc('messages.success_registered_coach'))
        this.onClose()
      } catch (error) {
        errorHandler(error)
      } finally {
        this.loading = false
      }
    },

    async onRemove (coach) {
      try {
        const tournament = await this.$store.dispatch('tournaments/removeCoach', {
          id: this.tournament?.id,
          coachId: coach.id
        })
        this.updateActiveTournament(tournament)
        this.$message.success(this.$tc('messages.success_removed_coach'))
      } catch (error) {
        errorHandler(error)
      } finally {
        this.loading = false
      }
    },

    updateActiveTournament (tournament) {
      if (tournament.id === this.tournament?.id) {
        this.$store.commit('app/setActiveTournament', tournament)
      }
    }
  },

  validations: {
    form: {
      coachId: { required }
    }
  }
}
</script>
